import { useMemo, useState } from "react";
import { ApiError } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/lib/permissions";
import { BANKING_PERMISSIONS } from "@folio/shared/permissions";
import { bankingApi } from "../api";
import { useBankingRequest } from "../hooks/useBankingRequest";
import type {
  BankingBatchDetailDto,
  BankingDuplicateDecisionDto,
  BankingConfirmationDto,
  BankingDuplicateCandidateDto,
  BankingNormalizedMovementDto,
} from "../types";
import {
  BankingError,
  BankingIssues,
  BankingPanel,
  BankingTotals,
} from "./BankingShared";

type Choices = Record<number, "keep" | "discard">;
function storedChoices(batch: BankingBatchDetailDto): Choices {
  return Object.fromEntries(
    batch.decisions.map((d) => [d.sourceRow, d.action]),
  );
}
export function duplicateDecisions(
  batch: BankingBatchDetailDto,
  choices: Choices,
): BankingDuplicateDecisionDto[] {
  const ids = new Map<number, string[]>();
  for (const candidate of batch.candidates) {
    const row = ids.get(candidate.sourceRow) ?? [];
    row.push(candidate.id);
    ids.set(candidate.sourceRow, row);
  }
  return [...ids]
    .filter(([row]) => choices[row])
    .map(([sourceRow, candidateIds]) => ({
      sourceRow,
      action: choices[sourceRow]!,
      candidateIds: candidateIds.sort(),
    }));
}
function Evidence({
  movement,
  currency,
}: {
  movement: BankingNormalizedMovementDto;
  currency: string;
}) {
  return (
    <span>
      {movement.date} · {movement.description} · Referencia{" "}
      {movement.reference ?? "—"} ·{" "}
      {movement.direction === "outflow" ? "Cargo" : "Abono"} {movement.amount}{" "}
      {currency} · Cuenta {movement.accountNumber} · {movement.counterpartRut}{" "}
      {movement.counterpartName}
    </span>
  );
}

export function BankingImportDetail({
  initial,
  onChanged,
}: {
  initial: BankingBatchDetailDto;
  onChanged?: () => void;
}) {
  const [batch, setBatch] = useState(initial);
  const [choices, setChoices] = useState<Choices>(() => storedChoices(initial));
  const [dirty, setDirty] = useState(false);
  const [receipt, setReceipt] = useState<BankingConfirmationDto | null>(null);
  const [rowPage, setRowPage] = useState(0);
  const [candidatePage, setCandidatePage] = useState(0);
  const { hasPermission, isLoading } = usePermissions();
  const write = !isLoading && hasPermission(BANKING_PERMISSIONS.importWrite);
  const request = useBankingRequest();
  const confirmed = batch.status === "confirmed";
  const stale =
    request.error instanceof ApiError &&
    (request.error.data as { code?: string } | undefined)?.code ===
      "PREVIEW_STALE";
  const candidateGroups = useMemo(() => {
    const groups = new Map<number, BankingDuplicateCandidateDto[]>();
    for (const candidate of batch.candidates) {
      const group = groups.get(candidate.sourceRow) ?? [];
      group.push(candidate);
      groups.set(candidate.sourceRow, group);
    }
    return groups;
  }, [batch.candidates]);
  const candidateRows = [...candidateGroups.keys()];
  const resolved = candidateRows.every((row) => choices[row]);
  const apply = (next: BankingBatchDetailDto) => {
    setBatch(next);
    setChoices(storedChoices(next));
    setDirty(false);
    setReceipt(null);
    setRowPage(0);
    setCandidatePage(0);
    onChanged?.();
  };
  const canConfirm =
    write &&
    !request.pending &&
    !stale &&
    batch.canConfirm &&
    !dirty &&
    resolved;
  return (
    <div className="space-y-4">
      <BankingPanel title={`Importación: ${batch.filename}`}>
        <p>
          Estado: {batch.status} · Revisión {batch.previewRevision}
        </p>
        {confirmed && (
          <p role="status">
            Archivo ya importado. No se crearán movimientos adicionales.
          </p>
        )}
        <p>
          {batch.account.bankLabel} · {batch.account.name} · Cuenta{" "}
          {batch.account.accountNumber} · {batch.account.currency}
        </p>
        <p>
          Cuenta contable histórica: {batch.account.accountingAccountCode} —{" "}
          {batch.account.accountingAccountName} · Revisión de cuenta{" "}
          {batch.account.accountRevision}
        </p>
        <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {Object.entries(batch.counts).map(([key, value]) => (
            <div key={key}>
              <dt>
                {
                  (
                    {
                      source: "Filas de origen",
                      effective: "Movimientos efectivos",
                      ignored: "Ignoradas",
                      valid: "Válidas",
                      invalid: "Inválidas",
                      candidate: "Filas candidatas",
                      accepted: "Aceptadas",
                      discarded: "Descartadas",
                    } as Record<string, string>
                  )[key]
                }
              </dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <BankingTotals
          label="Vista previa"
          totals={batch.previewTotals}
          currency={batch.account.currency}
        />
        <BankingTotals
          label="Aceptados"
          totals={batch.acceptedTotals}
          currency={batch.account.currency}
        />
        {batch.confirmedTotals && (
          <BankingTotals
            label="Confirmados"
            totals={batch.confirmedTotals}
            currency={batch.account.currency}
          />
        )}
        <p>
          Creado por {batch.createdBy} · {batch.createdAt}
        </p>
        <p>
          Actualizado: {batch.updatedAt} · Confirmado por{" "}
          {batch.confirmedBy ?? "—"} · {batch.confirmedAt ?? "—"}
        </p>
        <p className="break-all text-xs">
          SHA-256: {batch.sourceHash} · Adaptador {batch.adapterId} v
          {batch.adapterVersion}
        </p>
        <BankingIssues issues={batch.errors} />
        <BankingError error={request.error} />
        {receipt && (
          <div role="status">
            <p>
              Confirmación registrada: {receipt.batchId} · {receipt.confirmedBy}{" "}
              · {receipt.confirmedAt} · {receipt.counts.accepted} movimientos
            </p>
            <BankingTotals
              label="Importados"
              totals={receipt.totals}
              currency={batch.account.currency}
            />
          </div>
        )}
        {!write && (
          <p>
            Solo consulta: no tienes permiso para cargar, decidir ni confirmar.
          </p>
        )}
        {write && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={request.pending}
              onClick={() =>
                void request.run(
                  (signal) => bankingApi.detail(batch.id, signal),
                  apply,
                )
              }
            >
              Recargar detalle
            </Button>
            {!confirmed && (
              <Button
                variant="outline"
                disabled={request.pending}
                onClick={() =>
                  void request.run(
                    (signal) => bankingApi.refresh(batch, signal),
                    apply,
                    write,
                  )
                }
              >
                Actualizar vista previa y borrar decisiones
              </Button>
            )}
            <Button
              disabled={confirmed ? !write || request.pending : !canConfirm}
              onClick={() =>
                void request.run(
                  (signal) => bankingApi.confirm(batch, signal),
                  (result) => {
                    setReceipt(result);
                    setBatch((current) => ({
                      ...current,
                      status: "confirmed",
                      canConfirm: false,
                      counts: result.counts,
                      confirmedTotals: result.totals,
                      confirmedBy: result.confirmedBy,
                      confirmedAt: result.confirmedAt,
                    }));
                    onChanged?.();
                  },
                  confirmed ? write : canConfirm,
                )
              }
            >
              {confirmed
                ? "Verificar confirmación registrada"
                : "Confirmar importación completa"}
            </Button>
          </div>
        )}
        {!confirmed && !batch.canConfirm && (
          <p>
            La confirmación requiere un archivo válido con movimientos efectivos
            y todas las decisiones guardadas.
          </p>
        )}
        {dirty && (
          <p role="status">
            Guarda las decisiones para recalcular los totales y habilitar la
            confirmación.
          </p>
        )}
      </BankingPanel>
      <BankingPanel title="Filas normalizadas">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {[
                  "Fila",
                  "Estado",
                  "Fecha",
                  "Cuenta",
                  "Descripción",
                  "Referencia",
                  "Cargo",
                  "Abono",
                  "Contraparte",
                  "Errores",
                ].map((h) => (
                  <th className="p-2" key={h}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batch.rows.slice(rowPage * 50, (rowPage + 1) * 50).map((row) => (
                <tr className="border-t align-top" key={row.sourceRow}>
                  <td className="p-2">{row.sourceRow}</td>
                  <td className="p-2">
                    {row.ignored
                      ? "Ignorada"
                      : row.errors.length
                        ? "Inválida"
                        : "Válida"}
                  </td>
                  <td className="p-2">{row.movement?.date}</td>
                  <td className="p-2">{row.movement?.accountNumber}</td>
                  <td className="p-2">{row.movement?.description}</td>
                  <td className="p-2">{row.movement?.reference}</td>
                  <td className="p-2">
                    {row.movement?.direction === "outflow"
                      ? row.movement.amount
                      : "—"}
                  </td>
                  <td className="p-2">
                    {row.movement?.direction === "inflow"
                      ? row.movement.amount
                      : "—"}{" "}
                    {batch.account.currency}
                  </td>
                  <td className="p-2">
                    {row.movement?.counterpartRut}{" "}
                    {row.movement?.counterpartName}
                  </td>
                  <td className="p-2">
                    <BankingIssues issues={row.errors} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={rowPage === 0}
            onClick={() => setRowPage((p) => p - 1)}
          >
            Filas anteriores
          </Button>
          <span>Página {rowPage + 1}</span>
          <Button
            variant="outline"
            disabled={(rowPage + 1) * 50 >= batch.rows.length}
            onClick={() => setRowPage((p) => p + 1)}
          >
            Más filas
          </Button>
        </div>
      </BankingPanel>
      <BankingPanel title="Candidatos a duplicado">
        <p>
          Revisa cada coincidencia. Conservar como distinto mantiene el
          movimiento; ninguna coincidencia se elimina automáticamente. Una
          decisión por fila cubre todos sus candidatos.
        </p>
        {!candidateRows.length && <p>Sin candidatos.</p>}
        {candidateRows
          .slice(candidatePage * 25, (candidatePage + 1) * 25)
          .map((row) => (
            <fieldset className="space-y-2 rounded border p-3" key={row}>
              <legend>Fila {row}</legend>
              {candidateGroups.get(row)?.map((c) => (
                <div key={c.id} className="space-y-1 text-sm">
                  <p className="break-all">
                    Candidato {c.id} · {c.reasons.join(", ")}
                  </p>
                  <p>
                    Origen:{" "}
                    <Evidence
                      movement={c.evidence.source}
                      currency={c.evidence.currency}
                    />
                  </p>
                  <p>
                    Coincide con{" "}
                    {c.peer.kind === "source_row"
                      ? `fila ${c.peer.sourceRow}`
                      : `movimiento ${c.peer.movementId}`}
                    :{" "}
                    <Evidence
                      movement={c.evidence.peer}
                      currency={c.evidence.currency}
                    />
                  </p>
                </div>
              ))}
              <div className="flex gap-4">
                {(
                  [
                    ["keep", "Conservar como distinto"],
                    ["discard", "Descartar"],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`decision-${batch.id}-${row}`}
                      value={value}
                      checked={choices[row] === value}
                      disabled={!write || confirmed || request.pending}
                      onChange={() => {
                        setChoices((c) => ({ ...c, [row]: value }));
                        setDirty(true);
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {!choices[row] && <p>Decisión pendiente</p>}
            </fieldset>
          ))}
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={candidatePage === 0}
            onClick={() => setCandidatePage((p) => p - 1)}
          >
            Candidatos anteriores
          </Button>
          <span>Página {candidatePage + 1}</span>
          <Button
            variant="outline"
            disabled={(candidatePage + 1) * 25 >= candidateRows.length}
            onClick={() => setCandidatePage((p) => p + 1)}
          >
            Más candidatos
          </Button>
        </div>
        {write && !confirmed && candidateRows.length > 0 && (
          <Button
            disabled={request.pending || !resolved || !dirty}
            onClick={() =>
              void request.run(
                (signal) =>
                  bankingApi.decide(
                    batch,
                    duplicateDecisions(batch, choices),
                    signal,
                  ),
                apply,
                write && resolved && dirty,
              )
            }
          >
            Guardar todas las decisiones
          </Button>
        )}
        <h3 className="font-medium">Decisiones guardadas</h3>
        {!batch.decisions.length && <p>Sin decisiones guardadas.</p>}
        {batch.decisions.map((d) => (
          <p key={d.sourceRow} className="break-all text-sm">
            Fila {d.sourceRow}:{" "}
            {d.action === "keep" ? "Conservar como distinto" : "Descartar"} ·{" "}
            {d.decidedBy} · {d.decidedAt} · Revisión {d.previewRevision} ·
            Candidatos: {d.candidateIds.join(", ")}
          </p>
        ))}
      </BankingPanel>
    </div>
  );
}
